import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, X } from "lucide-react";
import api from "../../lib/api";
import ImageUpload from "../../components/ImageUpload";

export default function ClientsManager({ onChange }) {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", logo: "", order: 0 });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/site");
      setList(data.clients || []);
    } catch { toast.error("Could not load clients"); }
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing("new"); setForm({ name: "", logo: "", order: list.length + 1 }); };
  const startEdit = (c) => { setEditing(c.id); setForm({ name: c.name || "", logo: c.logo || "", order: c.order || 0 }); };
  const cancel = () => { setEditing(null); setForm({ name: "", logo: "", order: 0 }); };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, order: Number(form.order) || 0, logo: form.logo || null };
      if (editing === "new") await api.post("/admin/clients", payload);
      else await api.patch(`/admin/clients/${editing}`, payload);
      toast.success("Saved");
      cancel(); load(); onChange && onChange();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete client "${c.name}"?`)) return;
    try { await api.delete(`/admin/clients/${c.id}`); toast.success("Deleted"); load(); onChange && onChange(); }
    catch { toast.error("Delete failed"); }
  };

  if (editing !== null) {
    return (
      <div data-testid="client-editor" className="border border-[#1F3550] bg-[#0D1B2A] p-6 lg:p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-heading font-bold text-xl text-[#F8FAFC]">{editing === "new" ? "New Client" : "Edit Client"}</h3>
          <button onClick={cancel} data-testid="client-cancel" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#94A3B8] hover:text-[#F8FAFC]"><X className="h-4 w-4" /> Cancel</button>
        </div>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2"><Label>Client Name *</Label><input data-testid="client-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-2 w-full bg-[#050A0F] border border-[#1F3550] text-[#F8FAFC] focus:border-[#F39C12] focus:ring-1 focus:ring-[#F39C12] px-4 py-2.5 outline-none" /></div>
          <div><Label>Order</Label><input type="number" data-testid="client-order" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="mt-2 w-full bg-[#050A0F] border border-[#1F3550] text-[#F8FAFC] focus:border-[#F39C12] focus:ring-1 focus:ring-[#F39C12] px-4 py-2.5 outline-none" /></div>
          <div className="md:col-span-2">
            <ImageUpload label="Logo (optional — name shown if blank)" value={form.logo} onChange={(url) => setForm((f) => ({ ...f, logo: url }))} testid="client-logo" />
          </div>
          <div className="md:col-span-2"><button type="submit" disabled={saving} data-testid="client-save" className="inline-flex items-center gap-2 bg-[#F39C12] text-[#050A0F] px-6 py-2.5 font-bold uppercase tracking-widest text-xs hover:bg-[#D68910] disabled:opacity-50"><Save className="h-4 w-4" /> {saving ? "Saving..." : "Save"}</button></div>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="clients-manager">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading font-bold text-xl text-[#F8FAFC]">{list.length} clients</h2>
        <button onClick={startCreate} data-testid="client-new" className="inline-flex items-center gap-2 bg-[#F39C12] text-[#050A0F] px-5 py-2.5 font-bold uppercase tracking-widest text-xs hover:bg-[#D68910]"><Plus className="h-4 w-4" /> New Client</button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {list.map((c) => (
          <div key={c.id} data-testid={`client-row-${c.id}`} className="border border-[#1F3550] bg-[#0D1B2A] p-4 flex flex-col gap-3">
            <div className="aspect-[3/2] flex items-center justify-center bg-[#050A0F] border border-[#1F3550]">
              {c.logo ? <img src={c.logo} alt={c.name} className="max-h-12 max-w-full object-contain" /> : <span className="font-heading font-bold text-sm text-[#94A3B8] text-center px-2">{c.name}</span>}
            </div>
            <div>
              <div className="font-heading font-bold text-sm text-[#F8FAFC]">{c.name}</div>
              <div className="font-mono text-[10px] text-[#64748B]">order: {c.order}</div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => startEdit(c)} data-testid={`client-edit-${c.id}`} className="flex-1 border border-[#1F3550] hover:border-[#F39C12] text-[#F8FAFC] py-1.5 text-[10px] uppercase tracking-widest font-bold transition-colors">Edit</button>
              <button onClick={() => remove(c)} data-testid={`client-delete-${c.id}`} className="border border-[#1F3550] hover:border-[#EF4444] hover:text-[#EF4444] text-[#94A3B8] px-2 transition-colors"><Trash2 className="h-3 w-3" /></button>
            </div>
          </div>
        ))}
        {list.length === 0 && <div className="col-span-full py-12 text-center text-[#94A3B8] border border-dashed border-[#1F3550]">No clients yet.</div>}
      </div>
    </div>
  );
}

function Label({ children }) { return <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#94A3B8]">{children}</label>; }
